import { useEffect, useState } from "react";
import { getBishops, getStaff, getPartners } from "@/lib/api";
import { useT } from "@/lib/t";

export default function About() {
  const t = useT();
  const [bishops, setBishops] = useState([]);
  const [staff, setStaff] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    getBishops().then(setBishops);
    getStaff().then(setStaff);
    getPartners().then(setPartners);
  }, []);

  const missionList = t("about.mission_list") || [];

  return (
    <div data-testid="about-page">
      <section className="bg-ccc-plum text-ccc-ivory py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-ccc-gold uppercase tracking-[0.3em] text-xs mb-4">{t("about.eyebrow")}</div>
          <h1 className="font-serif text-5xl sm:text-6xl mb-6">{t("about.hero_title")}</h1>
          <p className="max-w-3xl text-stone-300 text-lg leading-relaxed">
            {t("about.hero_body")}
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-20">
        <h2 className="font-serif text-4xl text-ccc-plum mb-4">{t("about.mission_title")}</h2>
        <p className="text-lg leading-relaxed text-ccc-charcoal">
          {t("about.mission_body")}
        </p>
        <div className="gilded-divider my-10"/>
        <p className="text-stone-700 leading-relaxed">
          {t("about.mission_intro")}
        </p>
        <ol className="list-decimal pl-6 mt-4 space-y-2 text-stone-700">
          {(Array.isArray(missionList) ? missionList : []).map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ol>
      </section>

      <section className="bg-white border-y border-stone-200 py-20" data-testid="board-of-governors">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-ccc-purple uppercase tracking-[0.3em] text-xs mb-2">{t("about.board_eyebrow")}</div>
          <h2 className="font-serif text-4xl text-ccc-plum mb-10">{t("about.board_title")}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {bishops.map(b => (
              <article key={b.id} data-testid={`bishop-${b.slug}`} className="border border-stone-200 bg-ccc-ivory">
                <div className="aspect-[3/4] overflow-hidden bg-ccc-plum">
                  <img src={b.photo} alt={b.name} className="w-full h-full object-cover"
                    style={{ filter: "sepia(15%) saturate(90%)"}}/>
                </div>
                <div className="p-5">
                  <div className="text-xs uppercase tracking-wider text-ccc-gold-dark mb-1">{b.role}</div>
                  <h3 className="font-serif text-xl text-ccc-plum leading-tight mb-1">{b.name}</h3>
                  <div className="text-sm text-stone-600 mb-3">{b.title}</div>
                  <p className="text-sm text-stone-700 leading-relaxed">{b.bio}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="team">
        <div className="text-ccc-purple uppercase tracking-[0.3em] text-xs mb-2">{t("about.staff_eyebrow")}</div>
        <h2 className="font-serif text-4xl text-ccc-plum mb-10">{t("about.staff_title")}</h2>
        <div className="grid md:grid-cols-2 gap-8">
          {staff.map(s => (
            <div key={s.id} data-testid={`staff-${s.id}`} className="flex gap-6 items-start">
              <img src={s.photo} alt={s.name} className="w-32 h-32 object-cover" style={{ filter: "sepia(10%)" }}/>
              <div>
                <div className="text-xs uppercase tracking-wider text-ccc-gold-dark mb-1">{s.title}</div>
                <h3 className="font-serif text-2xl text-ccc-plum">{s.name}</h3>
                <p className="text-sm text-stone-700 mt-2 leading-relaxed">{s.bio}</p>
                <a href={`mailto:${s.email}`} className="text-ccc-purple text-sm mt-2 inline-block underline">{s.email}</a>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-ccc-plum text-ccc-ivory py-20" data-testid="partners">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-ccc-gold uppercase tracking-[0.3em] text-xs mb-2">{t("about.partners_eyebrow")}</div>
          <h2 className="font-serif text-4xl mb-8">{t("about.partners_title")}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {partners.map(p => (
              <a key={p.id} href={p.url} target="_blank" rel="noreferrer"
                className="block border border-purple-900 hover:border-ccc-gold p-5 transition-colors">
                <div className="font-serif text-xl text-ccc-gold">{p.name}</div>
                <div className="text-sm text-stone-300 mt-1">{p.description}</div>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
